import { debounce } from 'lodash-es'
import React, { useCallback, useState } from 'react'
import { Controller, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form'
import { BaseInputElement as BaseInput, BaseInputProps } from './Input'
import { SelectableOptionsPopover, SelectOptionProp } from './SelectableOptionsPopover'

interface BaseComboboxProps<T = string> extends Omit<BaseInputProps, 'onChange' | 'value' | 'onSelect'> {
  value?: string
  onChange?: (value: string) => void
  onSelect?: (option: SelectOptionProp<T>) => void
  options: SelectOptionProp<T>[]
  optionsLoading: boolean
  debounceMs?: number
  label?: string
}

export const BaseCombobox = <T,>({ value = '', onChange, onClear, onSelect, options, optionsLoading, debounceMs = 300, label, ...props }: BaseComboboxProps<T>) => {
  const [localValue, setLocalValue] = useState(value)
  const [showDropdown, setShowDropdown] = useState(false)

  const debouncedOnChange = onChange ? debounce(onChange, debounceMs) : undefined

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value)
    if (debouncedOnChange) debouncedOnChange(e.target.value)
  }

  const handleSelect = useCallback(
    (option: SelectOptionProp) => {
      const typedOption = option as SelectOptionProp<T>
      setLocalValue(typedOption.label)
      onSelect?.(typedOption)
    },
    [onSelect]
  )

  return (
    <SelectableOptionsPopover
      open={showDropdown}
      setOpen={setShowDropdown}
      onChange={handleSelect}
      options={options}
      optionsLoading={optionsLoading}
      trigger={
        <BaseInput
          {...props}
          onClear={() => {
            setLocalValue('')
            onChange?.('')
          }}
          iconLeft={'search'}
          value={localValue}
          onChange={handleInputChange}
          placeholder={label}
          labelIsPlaceholder={true}
          autoComplete="off"
        />
      }
      maxWidthPopoverSameAsTrigger={true}
    />
  )
}

// Form-controlled version
type ComboboxPropsForm<T extends FieldValues, U extends FieldPath<T>> = Omit<BaseComboboxProps, 'value' | 'onChange' | 'ref'> &
  Omit<UseControllerProps<T, U>, 'defaultValue'> & {
    onChange?: (value: string) => void
  }

export const Combobox = <T extends FieldValues, U extends FieldPath<T>>({ name, control, rules, shouldUnregister, onChange, ...props }: ComboboxPropsForm<T, U>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({ field: { ref, value, onChange: fieldOnChange, ...field } }) => (
        <BaseCombobox
          {...props}
          {...field}
          value={value}
          onChange={(v) => {
            fieldOnChange(v)
            onChange?.(v)
          }}
        />
      )}
    />
  )
}
